import { useState } from 'react';
import { clsx } from 'clsx';
import {
  CheckCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Stethoscope,
  Search,
  AlertCircle,
} from 'lucide-react';

// Normalize priority values from various LLM output formats to standard values
// Handles: P1_CRITICAL → critical, P2_IMPORTANT → high, P3_PREFERRED → medium, etc.
function normalizePriority(priority: string | undefined): 'critical' | 'high' | 'medium' | 'low' {
  if (!priority) return 'medium';
  const p = priority.toLowerCase();
  if (p === 'critical' || p.includes('p1') || p.includes('critical')) return 'critical';
  if (p === 'high' || p.includes('p2') || p.includes('important')) return 'high';
  if (p === 'medium' || p.includes('p3') || p.includes('preferred')) return 'medium';
  if (p === 'low' || p.includes('p4') || p.includes('optional')) return 'low';
  return 'medium'; // default fallback
}

interface Issue {
  severity: string;
  description: string;
  recommendation?: string;
}

interface Criterion {
  id: string;
  field: string;
  value: string | string[];
  priority: string;
  reasoning?: string;
  name?: string;
}

interface Indication {
  suitability: string;
  confidence: number;
  summary: string;
  issues: Issue[];
}

interface Criteria {
  criteria: Criterion[];
  priorityExplanation?: string;
}

interface Execution {
  id: string;
  blindCode: string;
  indication?: Indication;
  criteria?: Criteria;
}

interface IterationComparisonCardProps {
  executions: Execution[];
  highlightDifferences?: boolean;
}

// Helper to check if values are the same across all executions
function areAllSame<T>(values: T[]): boolean {
  if (values.length === 0) return true;
  const first = JSON.stringify(values[0]);
  return values.every((v) => JSON.stringify(v) === first);
}

// Helper to get suitability color
function getSuitabilityColor(suitability: string): string {
  switch (suitability?.toUpperCase()) {
    case 'SUITABLE':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'UNSUITABLE':
      return 'bg-red-100 text-red-700 border-red-200';
    default:
      return 'bg-amber-100 text-amber-700 border-amber-200';
  }
}

// Helper to get priority color
function getPriorityColor(priority: string): string {
  switch (priority?.toLowerCase()) {
    case 'critical':
      return 'bg-red-100 text-red-700';
    case 'high':
      return 'bg-orange-100 text-orange-700';
    case 'medium':
      return 'bg-amber-100 text-amber-700';
    case 'low':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

// Comparison indicator
function ComparisonIndicator({ isSame }: { isSame: boolean }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
        isSame ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
      )}
    >
      {isSame ? (
        <>
          <CheckCircle className="h-3 w-3" />
          Same
        </>
      ) : (
        <>
          <AlertTriangle className="h-3 w-3" />
          Varies
        </>
      )}
    </span>
  );
}

export function IterationComparisonCard({
  executions,
  highlightDifferences = true,
}: IterationComparisonCardProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['suitability', 'criteria'])
  );

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  if (executions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <AlertCircle className="h-8 w-8 mx-auto mb-2" />
        <p>No executions to compare</p>
      </div>
    );
  }

  // Extract values for comparison
  const suitabilityValues = executions.map((e) => e.indication?.suitability || 'N/A');
  const suitabilitySame = areAllSame(suitabilityValues);

  // Get all unique criteria fields across executions
  const allCriteriaFields = new Set<string>();
  executions.forEach((e) => {
    e.criteria?.criteria?.forEach((c) => {
      allCriteriaFields.add(c.field || c.name || c.id);
    });
  });

  return (
    <div className="space-y-4">
      {/* Suitability Comparison */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <button
          onClick={() => toggleSection('suitability')}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-gray-600" />
            <span className="font-semibold text-gray-900">Suitability Assessment</span>
            {highlightDifferences && <ComparisonIndicator isSame={suitabilitySame} />}
          </div>
          {expandedSections.has('suitability') ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </button>

        {expandedSections.has('suitability') && (
          <div className="p-4">
            {/* Column headers */}
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${executions.length}, 1fr)` }}>
              {executions.map((exec) => (
                <div key={exec.id} className="text-center">
                  <span className="inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-700">
                    {exec.blindCode}
                  </span>
                </div>
              ))}
            </div>

            {/* Suitability values */}
            <div className="mt-4 grid gap-4" style={{ gridTemplateColumns: `repeat(${executions.length}, 1fr)` }}>
              {executions.map((exec) => (
                <div
                  key={exec.id}
                  className={clsx(
                    'p-3 rounded-lg border text-center',
                    getSuitabilityColor(exec.indication?.suitability || '')
                  )}
                >
                  <p className="text-lg font-bold">
                    {exec.indication?.suitability?.replace('_', ' ') || 'N/A'}
                  </p>
                  <p className="text-sm opacity-75">
                    {exec.indication?.confidence || 0}% confidence
                  </p>
                </div>
              ))}
            </div>

            {/* Summary comparison */}
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Summary Comparison</h4>
              <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${executions.length}, 1fr)` }}>
                {executions.map((exec) => (
                  <div key={exec.id} className="p-3 rounded-lg bg-gray-50 text-sm text-gray-600">
                    {exec.indication?.summary || 'No summary available'}
                  </div>
                ))}
              </div>
            </div>

            {/* Issues comparison */}
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Issues Identified</h4>
              <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${executions.length}, 1fr)` }}>
                {executions.map((exec) => (
                  <div key={exec.id} className="space-y-2">
                    {exec.indication?.issues?.length ? (
                      exec.indication.issues.map((issue, idx) => (
                        <div
                          key={idx}
                          className={clsx(
                            'p-2 rounded text-xs',
                            issue.severity === 'critical'
                              ? 'bg-red-50 text-red-700'
                              : issue.severity === 'high'
                              ? 'bg-orange-50 text-orange-700'
                              : 'bg-amber-50 text-amber-700'
                          )}
                        >
                          <span className="font-medium capitalize">{issue.severity}:</span>{' '}
                          {issue.description}
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-gray-400 italic">No issues</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Criteria Comparison */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <button
          onClick={() => toggleSection('criteria')}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Search className="h-5 w-5 text-gray-600" />
            <span className="font-semibold text-gray-900">Matching Criteria</span>
          </div>
          {expandedSections.has('criteria') ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </button>

        {expandedSections.has('criteria') && (
          <div className="p-4">
            {/* Column headers */}
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${executions.length}, 1fr)` }}>
              {executions.map((exec) => (
                <div key={exec.id} className="text-center">
                  <span className="inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-700">
                    {exec.blindCode}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    {exec.criteria?.criteria?.length || 0} criteria
                  </p>
                </div>
              ))}
            </div>

            {/* Criteria by priority */}
            {['critical', 'high', 'medium', 'low'].map((priority) => {
              const hasCriteriaForPriority = executions.some(
                (e) => e.criteria?.criteria?.some((c) => normalizePriority(c.priority) === priority)
              );

              if (!hasCriteriaForPriority) return null;

              return (
                <div key={priority} className="mt-4">
                  <h4
                    className={clsx(
                      'text-sm font-semibold capitalize mb-2',
                      priority === 'critical'
                        ? 'text-red-700'
                        : priority === 'high'
                        ? 'text-orange-700'
                        : priority === 'medium'
                        ? 'text-amber-700'
                        : 'text-gray-600'
                    )}
                  >
                    {priority} Priority
                  </h4>
                  <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${executions.length}, 1fr)` }}>
                    {executions.map((exec) => {
                      const priorityCriteria = exec.criteria?.criteria?.filter(
                        (c) => normalizePriority(c.priority) === priority
                      );

                      return (
                        <div key={exec.id} className="space-y-2">
                          {priorityCriteria?.length ? (
                            priorityCriteria.map((criterion, idx) => (
                              <div
                                key={idx}
                                className={clsx('p-2 rounded text-xs', getPriorityColor(priority))}
                              >
                                <p className="font-medium">{criterion.name || criterion.field}</p>
                                <p className="opacity-75">
                                  {Array.isArray(criterion.value)
                                    ? criterion.value.join(', ')
                                    : criterion.value}
                                </p>
                              </div>
                            ))
                          ) : (
                            <p className="text-xs text-gray-400 italic">None</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export type { Execution, Indication, Criteria, Criterion, Issue };
